import React from 'react';
import { object, func, string, arrayOf, number } from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { Formik, Form, Field } from 'formik';

import FormItem from '../../core/FormItem';
import Fieldset from '../../core/Fieldset';
import FormikButtonSelectField from '../../core/formik/FormikButtonSelectField';
import StepContainer from '../steps/StepContainer';
import TrusteeForm from './TrusteeForm';
import DeedFileForm from '../../deedfileform/DeedFileForm';
import AbnSearch from '../../abn/abnsearch/AbnSearch';
import CompositeAlert from '../../core/CompositeAlert';

import { trusteeSchema, validateFilesLength } from './validationSchema';

// eslint-disable-next-line no-unused-vars
const styles = (theme) => ({
  subtitle: {
    margin: theme.spacing(1.5, 0),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1, 0),
    },
  },
  subtitle2: {
    color: theme.palette.primary.main,
  },
  subtitle3: {
    fontSize: theme.typography.fontSize,
  },
});

class LocateYourTrustee extends React.PureComponent {
  componentDidMount() {
    this.props.reset();
  }

  render() {
    const {
      abn,
      classes,
      submitTrusteeDetails,
      isEligible,
      reset,
      validationResult,
      errorMessage,
      searchCompany,
      trusteeOptions,
      delay,
    } = this.props;

    return (
      <Formik
        enableReinitialize
        validateOnBlur={false}
        validationSchema={trusteeSchema(this.props)}
        initialValues={{
          isIndividual: null,
          trusteeNames: [
            {
              firstName: '',
              lastName: '',
            },
          ],
          files: [],
        }}
        onSubmit={(values, { setSubmitting }) => {
          const callback = () => {
            setSubmitting(false);
          };
          submitTrusteeDetails(values, callback);
        }}
        render={({ values, errors, handleSubmit, touched, isSubmitting }) => {
          const filesLengthError = validateFilesLength(values.files, values.isIndividual);
          const isSubmitDisable =
            isSubmitting ||
            (values.isIndividual === 'false' && !validationResult.isEligible) ||
            (values.isIndividual === 'true' && Object.keys(errors).length > 0);

          return (
            <StepContainer
              title="Locate your trustee"
              subtitle={`${abn?.entityName ?? 'Trustee'} is`}
              isSubmitVisible={values.isIndividual !== null}
              isSubmitDisabled={isSubmitDisable}
              onSubmit={handleSubmit}
            >
              <Form autoComplete="off">
                <Fieldset disabled={isSubmitting}>
                  <Grid container spacing={2}>
                    {errorMessage && (
                      <Grid item xs={12}>
                        <CompositeAlert variant="error" message={errorMessage} />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <FormItem>
                        <Field
                          name="isIndividual"
                          options={trusteeOptions}
                          component={FormikButtonSelectField}
                          data-testid="isIndividual"
                        />
                      </FormItem>
                    </Grid>
                    {values.isIndividual === 'true' && (
                      <React.Fragment>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" className={classes.subtitle}>
                            {'We need the full name of the individual trustee'}
                          </Typography>
                        </Grid>
                        <TrusteeForm values={values} data-testid="trusteeForm" />
                        <Grid item xs={12}>
                          <DeedFileForm
                            name="files"
                            values={values}
                            errors={errors}
                            touched={touched}
                            isRequired
                            data-testid="deedFileForm1"
                            filesLengthError={filesLengthError}
                          />
                        </Grid>
                      </React.Fragment>
                    )}
                    {values.isIndividual === 'false' && (
                      <React.Fragment>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1">
                            {'Please search for the company acting as a trustee'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <AbnSearch
                            onItemSelected={(data) => isEligible(data)}
                            onNewSearch={reset}
                            search={searchCompany}
                            data-testid="abnSearch"
                            delay={delay}
                            fixedIdLabelValue="ACN"
                          />
                        </Grid>
                        {(validationResult?.isValidated ?? false) && (
                          <React.Fragment>
                            <Grid item xs={12}>
                              <CompositeAlert
                                variant={validationResult.isEligible ? 'success' : 'error'}
                                message={validationResult.message}
                              />
                            </Grid>
                            {(validationResult?.isEligible ?? false) && (
                              <Grid item xs={12}>
                                <DeedFileForm
                                  values={values}
                                  errors={errors}
                                  touched={touched}
                                  data-testid="deedFileForm2"
                                  name="files"
                                  filesLengthError={filesLengthError}
                                />
                              </Grid>
                            )}
                          </React.Fragment>
                        )}
                      </React.Fragment>
                    )}
                  </Grid>
                </Fieldset>
              </Form>
            </StepContainer>
          );
        }}
      />
    );
  }
}

LocateYourTrustee.propTypes = {
  classes: object.isRequired,
  abn: object.isRequired,
  submitTrusteeDetails: func.isRequired,
  isEligible: func.isRequired,
  reset: func.isRequired,
  validationResult: object.isRequired,
  errorMessage: string,
  searchCompany: func.isRequired,
  trusteeOptions: arrayOf(object).isRequired,
  delay: number,
};

LocateYourTrustee.defaultProps = {
  errorMessage: '',
  delay: 500,
};

export default withStyles(styles, { withTheme: true })(LocateYourTrustee);
